<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractRevisionDropdown',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const otherRevisions = (...args) => {
    return _.filter(args[0], (p) => p.revision_number != args[1]);
  };

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedRevision = computed(() => {
    return _.find(props.contract.revisions, {
      revision_number: props.contract.revision_number,
    });
  });

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );

  const act_8358196963355853_navigate_8cw77bp23_getActionArgs = async (
    $event
  ) => {
    return {
      location: {
        name: 'app.contract',
        query: {
          rev: $event.scope.revision.revision_number,
        },
        params: {
          id: props.contract.id,
        },
      },
    };
  };
  const act_8356612450665535_showModal_gmz3ph50n_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateContractRevision',
      props: {
        contract: _.clone(props.contract),
      },
    };
  };
  const act_1382594685840889_closeComponent_dblxbv5ch_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };
  const act_1382594685840889_request_jegptubva_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_contract',
      data: props.contract,
      parameters: {
        rev: props.contract.revision_number,
      },
    };
  };
  const act_1382594685840889_request_jegptubva_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_8358196963355853_navigate_8cw77bp23 =
    new actionTypes.NavigateAction({
      actionArgs: act_8358196963355853_navigate_8cw77bp23_getActionArgs,
    });
  const inst_evh_8358196963355853_clickPreventStop_txpan5967 =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_navigate_8cw77bp23,
      event: 'click.prevent.stop',
      displayName: 'navigate',
    });
  const inst_evh_8358196963355853_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8358196963355853_clickPreventStop_txpan5967],
      event: 'click.prevent.stop',
    });
  const act_8356612450665535_reloadComponentData_qebz93qbf =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8356612450665535_success_bmpzzw7mf =
    new actionTypes.EventHandlerImpl({
      action: act_8356612450665535_reloadComponentData_qebz93qbf,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_8356612450665535_showModal_gmz3ph50n =
    new actionTypes.ShowModalAction({
      actionArgs: act_8356612450665535_showModal_gmz3ph50n_getActionArgs,
      events: [inst_evh_8356612450665535_success_bmpzzw7mf],
    });
  const inst_evh_8356612450665535_click_kzdhuk1n1 =
    new actionTypes.EventHandlerImpl({
      action: act_8356612450665535_showModal_gmz3ph50n,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_8356612450665535_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8356612450665535_click_kzdhuk1n1],
    event: 'click',
  });
  const act_1382594685840889_closeComponent_dblxbv5ch =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1382594685840889_closeComponent_dblxbv5ch_getActionArgs,
    });
  const inst_evh_1382594685840889_success_jpe90us5x =
    new actionTypes.EventHandlerImpl({
      action: act_1382594685840889_closeComponent_dblxbv5ch,
      event: 'success',
      displayName: 'closeComponent',
    });
  const act_1382594685840889_request_jegptubva = new actionTypes.RequestAction({
    actionArgs: act_1382594685840889_request_jegptubva_getActionArgs,
    confirm: act_1382594685840889_request_jegptubva_getConfirm,
    events: [inst_evh_1382594685840889_success_jpe90us5x],
  });
  const inst_evh_1382594685840889_click_e1fmprxik =
    new actionTypes.EventHandlerImpl({
      action: act_1382594685840889_request_jegptubva,
      event: 'click',
      displayName: 'request',
    });
  const inst_evh_1382594685840889_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1382594685840889_click_e1fmprxik],
    event: 'click',
  });

  const actions = {
    inst_evh_8358196963355853_clickPreventStop_txpan5967,
    inst_evh_8358196963355853_clickPreventStop,
    inst_evh_8356612450665535_success_bmpzzw7mf,
    inst_evh_8356612450665535_click_kzdhuk1n1,
    inst_evh_8356612450665535_click,
    inst_evh_1382594685840889_success_jpe90us5x,
    inst_evh_1382594685840889_click_e1fmprxik,
    inst_evh_1382594685840889_click,
  };

  const evh_8358196963355853_clickPreventStop_txpan5967 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8358196963355853_clickPreventStop_txpan5967.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8356612450665535_success_bmpzzw7mf = async (event, scope) => {
    return await actions.inst_evh_8356612450665535_success_bmpzzw7mf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8356612450665535_click_kzdhuk1n1 = async (event, scope) => {
    return await actions.inst_evh_8356612450665535_click_kzdhuk1n1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8356612450665535_click = async (event, scope) => {
    return await actions.inst_evh_8356612450665535_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1382594685840889_success_jpe90us5x = async (event, scope) => {
    return await actions.inst_evh_1382594685840889_success_jpe90us5x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1382594685840889_click_e1fmprxik = async (event, scope) => {
    return await actions.inst_evh_1382594685840889_click_e1fmprxik.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1382594685840889_click = async (event, scope) => {
    return await actions.inst_evh_1382594685840889_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <BDropdown size="sm" toggle-class="btn-light border" v-if="loaded">
    <template #button-content>
      <UcContractRevisionDropdownItem :revisionInfo="selectedRevision">
      </UcContractRevisionDropdownItem>
    </template>
    <hr v-if="contract.revisions.length > 1" />
    <BDropdownItemButton
      :key="revision.revision_number"
      v-for="revision in _.orderBy(contract.revisions, ['revision_number'])"
      @click.prevent.stop="
        evh_8358196963355853_clickPreventStop($event, { revision })
      "
    >
      <UcContractRevisionDropdownItem
        :revisionInfo="revision"
        :isSelected="contract.revision_number == revision.revision_number"
      >
      </UcContractRevisionDropdownItem>
    </BDropdownItemButton>
    <hr />
    <BDropdownItemButton @click="evh_8356612450665535_click($event, {})">
      <ItpText :text="pgettext('Contract', 'Create revision...')"> </ItpText>
    </BDropdownItemButton>
    <BDropdownItemButton
      v-if="contract.metadata.can_delete_revision"
      @click="evh_1382594685840889_click($event, {})"
    >
      <ItpText :text="pgettext('Contract', 'Delete revision')"> </ItpText>
    </BDropdownItemButton>
  </BDropdown>
</template>
